/**
 * PatientBalanceCard
 *
 * Displays a patient's total balance and a ledger transaction history.
 * Uses the billing ledger balance endpoint which returns paginated entries.
 */

import React, { useState } from 'react';
import {
  AccountBalanceWallet as BalanceIcon,
  CheckCircle as PaidIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { LedgerEntryItem } from '@store/generated/billing';

import { useGetPatientBalanceQuery } from '../store';

function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function formatEntryDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getEntryLabel(entry: LedgerEntryItem): string {
  if (entry.description) return entry.description;

  const { entry_type, party_type } = entry;
  if (entry_type === 'charge' && party_type === 'payer') return 'Billed to insurance';
  if (entry_type === 'payment' && party_type === 'payer') return 'Insurance payment';
  if (entry_type === 'adjustment' && party_type === 'payer') return 'Adjustment';
  if (entry_type === 'transfer' && party_type === 'patient') return 'Patient responsibility';
  if (entry_type === 'payment' && party_type === 'patient') return 'Patient payment';
  if (entry_type === 'charge' && party_type === 'patient') return 'Self-pay charge';
  if (entry_type === 'transfer' && party_type === 'payer') return 'Transfer';
  return `${entry_type} (${party_type})`;
}

function getEntryTypeChip(entry: LedgerEntryItem) {
  const { entry_type, party_type } = entry;

  if (entry_type === 'payment') {
    return <Chip label="Payment" size="small" color="success" variant="outlined" />;
  }
  if (entry_type === 'charge') {
    return <Chip label="Charge" size="small" color={party_type === 'patient' ? 'warning' : 'default'} variant="outlined" />;
  }
  if (entry_type === 'adjustment') {
    return <Chip label="Adjustment" size="small" color="info" variant="outlined" />;
  }
  if (entry_type === 'transfer') {
    return <Chip label="Transfer" size="small" variant="outlined" />;
  }
  return <Chip label={entry_type} size="small" variant="outlined" />;
}

interface PatientBalanceCardProps {
  patientId: string;
  /** Scope entries to a single encounter */
  encounterId?: string;
}

const COLLAPSED_COUNT = 5;

export const PatientBalanceCard: React.FC<PatientBalanceCardProps> = ({ patientId, encounterId }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading, error } = useGetPatientBalanceQuery({ patientId, encounterId });

  // No billing activity yet — don't render
  if (!isLoading && data && data.patient_billing_account_id == null && data.total_balance_cents === 0) {
    return null;
  }

  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.grey[200]}`, borderRadius: 3, p: 3 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  if (error || !data) {
    return (
      <Alert severity="error" sx={{ borderRadius: 3 }}>
        Failed to load patient balance.
      </Alert>
    );
  }

  const hasBalance = data.total_balance_cents !== 0;
  const hasEntries = data.entries && data.entries.length > 0;

  return (
    <Paper
      elevation={0}
      sx={{ border: `1px solid ${theme.palette.grey[200]}`, borderRadius: 3, p: 3 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <BalanceIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Patient Balance
          </Typography>
        </Stack>
        {!hasBalance && (
          <Chip
            icon={<PaidIcon sx={{ fontSize: 16 }} />}
            label="No balance"
            size="small"
            color="success"
            variant="outlined"
          />
        )}
      </Box>

      {hasBalance && (
        <Box sx={{ mb: hasEntries ? 2 : 0 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color:
                data.total_balance_cents > 0
                  ? theme.palette.warning.main
                  : theme.palette.success.main,
            }}
          >
            {formatCents(data.total_balance_cents)}
          </Typography>
        </Box>
      )}

      {hasEntries && (() => {
        const allEntries = data.entries ?? [];
        const visibleEntries = expanded ? allEntries : allEntries.slice(0, COLLAPSED_COUNT);
        const hiddenCount = allEntries.length - COLLAPSED_COUNT;

        return (
          <>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Type</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  Amount
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleEntries.map(entry => (
                <TableRow key={entry.entry_id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                      {formatEntryDate(entry.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                      {getEntryLabel(entry)}
                    </Typography>
                    {entry.source_type && (
                      <Typography variant="caption" color="text.disabled">
                        {entry.source_type.replace(/_/g, ' ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{getEntryTypeChip(entry)}</TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{
                        color:
                          entry.amount_cents > 0
                            ? theme.palette.warning.main
                            : entry.amount_cents < 0
                              ? theme.palette.success.main
                              : 'text.secondary',
                      }}
                    >
                      {entry.amount_cents > 0 ? '+' : ''}{formatCents(entry.amount_cents)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {hiddenCount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Button
              size="small"
              onClick={() => setExpanded(prev => !prev)}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transition: 'transform 0.2s',
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
              }
            >
              {expanded ? 'Show less' : `Show ${hiddenCount} more`}
            </Button>
          </Box>
        )}
          </>
        );
      })()}
    </Paper>
  );
};
